import React, { useState } from 'react';
import { X } from 'lucide-react';
import { PhoneInput } from './PhoneInput';
import './phone-input-styles.css';
interface GuideFormPopupProps {
  isOpen: boolean;
  onClose: () => void;
}
export const GuideFormPopup = ({
  isOpen,
  onClose
}: GuideFormPopupProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    website: ''
  });
  if (!isOpen) return null;
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handlePhoneChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      phone: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.website) {
      console.log('Bot detected via honeypot');
      return;
    }
    if (!isPhoneValid) return;
    setIsSubmitting(true);
    try {
      await fetch('https://hooks.zapier.com/hooks/catch/12304964/upn55f2/', {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fullName: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          formType: 'investor_guide',
          source: 'Guide Download Form'
        })
      });
    } catch (err) {
      console.error('Webhook error:', err);
    }
    setIsSubmitting(false);
    window.open('https://ikigaire.ae/wp-content/uploads/2026/03/WHY-DUBAI.pdf', '_blank');
    onClose();
    setFormData({
      fullName: '',
      email: '',
      phone: '',
      website: ''
    });
  };
  const inputClasses = 'w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#d8a34d]/20 focus:border-[#d8a34d] outline-none transition-all text-gray-900 bg-gray-50/50 focus:bg-white';
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-321">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity" onClick={onClose} data-id="element-322" />

      {/* Modal Content */}
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-323">
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100 flex justify-between items-start bg-white" data-id="element-324">
          <div data-id="element-325">
            <h3 className="text-xl md:text-2xl font-display font-bold text-[#1a2e1c] leading-tight pr-8" data-id="element-326">
              Get Your Free Dubai Investment Playbook
            </h3>
            <div className="w-12 h-1 bg-[#d8a34d] mt-3 rounded-full" data-id="element-327"></div>
            <p className="text-gray-500 text-sm mt-3" data-id="element-328">
              ROI projections, top areas, Golden Visa rules & tax benefits — all
              in one guide.
            </p>
          </div>
          <button onClick={onClose} className="absolute top-5 right-5 text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100" aria-label="Close modal" data-id="element-329">
            <X className="w-5 h-5" data-id="element-330" />
          </button>
        </div>

        {/* Form Body */}
        <div className="overflow-y-auto p-6" data-id="element-331">
          <form id="guide-form" onSubmit={handleSubmit} className="space-y-4" data-id="element-332">
            {/* Full Name */}
            <div className="space-y-1.5" data-id="element-333">
              <label htmlFor="fullName" className="block text-sm font-medium text-gray-700" data-id="element-334">
                Full Name *
              </label>
              <input type="text" id="fullName" name="fullName" required value={formData.fullName} onChange={handleChange} className={inputClasses} placeholder="John Doe" data-id="element-335" />
            </div>

            {/* Email */}
            <div className="space-y-1.5" data-id="element-336">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700" data-id="element-337">
                Email Address *
              </label>
              <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange} className={inputClasses} placeholder="john@example.com" data-id="element-338" />
            </div>

            {/* Phone */}
            <div className="space-y-1.5" data-id="element-339">
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700" data-id="element-340">
                Phone Number *
              </label>
              <PhoneInput id="phone" value={formData.phone} onChange={handlePhoneChange} onValidationChange={setIsPhoneValid} required placeholder="+1 (555) 000-0000" data-id="element-341" />
            </div>

            {/* Honeypot field - hidden from humans, visible to bots */}
            <div className="absolute -left-[9999px]" aria-hidden="true" data-id="element-342">
              <label htmlFor="guide-website" data-id="element-343">Website</label>
              <input type="text" id="guide-website" name="website" value={formData.website} onChange={handleChange} tabIndex={-1} autoComplete="off" data-id="element-344" />
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50" data-id="element-345">
          <button type="submit" form="guide-form" disabled={isSubmitting} className="w-full btn-gold px-6 py-3.5 rounded-lg font-bold text-base shadow-lg flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-346">
            {isSubmitting ? <>
                <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-347">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-348"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-349"></path>
                </svg>
                Processing...
              </> : 'Download Free Guide'}
          </button>
          <p className="text-center text-xs text-gray-500 mt-3" data-id="element-350">
            Your information is secure. We respect your privacy.
          </p>
        </div>
      </div>
    </div>;
};